import { createSignal } from 'solid-js';
import api from './api';

export interface News {
  id: string;
  title: string;
  content: string;
  type?: 'info' | 'warning' | 'success' | 'error';
  created_at?: string;
  read?: boolean;
}

// Reactive signals for news state
const [latestNews, setLatestNews] = createSignal<News | null>(null);
const [unreadCount, setUnreadCount] = createSignal(0);
const [showModal, setShowModal] = createSignal(false);

// Manage read news IDs in localStorage
const getReadNewsIds = (): Set<string> => {
  try {
    const stored = localStorage.getItem('read_news_ids');
    return new Set(stored ? JSON.parse(stored) : []);
  } catch {
    return new Set();
  }
};

const addReadNewsId = (newsId: string) => {
  const readIds = getReadNewsIds();
  readIds.add(newsId);
  localStorage.setItem('read_news_ids', JSON.stringify(Array.from(readIds)));
};

const isNewsRead = (newsId: string): boolean => {
  return getReadNewsIds().has(newsId);
};

/**
 * Fetch latest news/announcement
 * Gets the most recent unread news for the user
 * Auto-shows modal if new unread news is available
 */
export const getLatestNews = async (): Promise<News | null> => {
  try {
    const response = await api.get('/news/latest');

    // Check if the response has data (success: true, data: {...})
    if (response.data?.success && response.data?.data) {
      const newsItem = response.data.data;

      // Check if this news has already been read
      const alreadyRead = isNewsRead(newsItem.id);

      setLatestNews(newsItem);

      // Auto-show modal if news is unread
      if (!alreadyRead) {
        setShowModal(true);
        setUnreadCount(1);
      } else {
        setUnreadCount(0);
      }

      return newsItem;
    } else if (response.data?.success && !response.data?.data) {
      // No news available, but API call succeeded
      setLatestNews(null);
      setShowModal(false);
      setUnreadCount(0);
      return null;
    }

    return null;
  } catch (error: any) {
    // Gracefully handle endpoint not found or network errors
    if (error.response?.status === 404 || error.code === 'ERR_NETWORK') {
      return null;
    }
    return null;
  }
};

/**
 * Mark news as read
 * Saves to localStorage (frontend only, no backend sync)
 */
export const markNewsAsRead = async (newsId: string): Promise<boolean> => {
  try {
    // Save to localStorage
    addReadNewsId(newsId);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Get all news/announcements
 * Fetches paginated list of all news items
 */
export const getAllNews = async (limit = 10): Promise<News[]> => {
  try {
    const response = await api.get('/news', { params: { limit } });
    if (Array.isArray(response.data?.data)) {
      return response.data.data;
    }
    return [];
  } catch (error) {
    return [];
  }
};

/**
 * Export reactive state and modal controls
 */
export const newsState = {
  latestNews,
  unreadCount,
  showModal,
  setShowModal,
  markNewsAsRead,
};
